import { useState } from 'react';
import { Banner, Button, Modal } from '@immo/ui-kit';
import type { LandIqDataRunRetryRefusalReason } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { AlertCircleIcon, RefreshIcon, WarningTriangleIcon } from '@/components/layout/icons';
import { LANDIQ_PAGE_ERROR_CODES } from '@/components/landiq/landiq-error-state';

export type RetryOutcome =
  | { kind: 'retried'; movedChunks: number }
  | { kind: 'refused'; reason: LandIqDataRunRetryRefusalReason }
  | { kind: 'failed'; code: string | undefined };

type Props = {
  readonly entryId: string;
  readonly count: number;
  readonly onClose: () => void;
  readonly onSettled: (outcome: RetryOutcome) => void;
};

type State = { kind: 'idle' } | { kind: 'sending' } | Exclude<RetryOutcome, { kind: 'retried' }>;

const NO_LONGER_POSSIBLE = 'pages.admin.landiq-data-run.retry-failed-chunks-dialog.refusals.no-longer-possible';

const REFUSALS = {
  NOT_AN_IMPORT: { key: NO_LONGER_POSSIBLE, variant: 'warning' },
  NO_FAILED_CHUNKS: { key: NO_LONGER_POSSIBLE, variant: 'warning' },
  STORAGE_UNAVAILABLE: {
    key: 'pages.admin.landiq-data-run.retry-failed-chunks-dialog.refusals.STORAGE_UNAVAILABLE',
    variant: 'danger',
  },
  UNKNOWN_ENTRY: { key: 'pages.admin.landiq-data-runs.refusals.UNKNOWN_ENTRY', variant: 'danger' },
  ENVIRONMENT_NOT_ALLOWED: {
    key: 'pages.admin.landiq-data-runs.refusals.ENVIRONMENT_NOT_ALLOWED',
    variant: 'warning',
  },
} as const satisfies Record<LandIqDataRunRetryRefusalReason, { key: string; variant: 'warning' | 'danger' }>;

export function RetryFailedChunksDialog({ entryId, count, onClose, onSettled }: Props) {
  const t = useTranslations('pages.admin.landiq-data-run');
  const tAbsolute = useTranslations();
  const tErrors = useTranslations('errors');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const sending = state.kind === 'sending';
  const settled = state.kind === 'refused' || state.kind === 'failed';

  async function submit() {
    if (state.kind !== 'idle') return;

    setState({ kind: 'sending' });

    try {
      const result = await apiClient.adminLandiqDataRuns.retryFailedChunks({ pathParams: { entryId } });

      if (!result.ok) {
        const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;
        setState({ kind: 'failed', code });
        onSettled({ kind: 'failed', code });

        return;
      }

      if (result.body.outcome === 'RETRIED') {
        onSettled({ kind: 'retried', movedChunks: result.body.movedChunks });

        return;
      }

      setState({ kind: 'refused', reason: result.body.reason });
      onSettled({ kind: 'refused', reason: result.body.reason });
    } catch {
      setState({ kind: 'failed', code: undefined });
      onSettled({ kind: 'failed', code: undefined });
    }
  }

  return (
    <Modal
      open
      onClose={() => (sending ? undefined : onClose())}
      confirmOnClose={false}
      size="md"
      title={
        <span className="inline-flex items-center gap-2">
          <RefreshIcon aria-hidden className="h-4 w-4" />
          {t('retry-failed-chunks-dialog.title')}
        </span>
      }
      description={entryId}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose} disabled={sending}>
            {t('retry-failed-chunks-dialog.cancel')}
          </Button>
          {!settled && (
            <Button variant="primary" type="button" onClick={() => void submit()} loading={sending} disabled={sending}>
              {t('retry-failed-chunks-dialog.submit')}
            </Button>
          )}
        </>
      }
    >
      <p className="text-sm text-text-secondary">{t('retry-failed-chunks-dialog.body', { count })}</p>
      {state.kind === 'refused' && (
        <div className="mt-4">
          <Banner
            variant={REFUSALS[state.reason].variant}
            icon={REFUSALS[state.reason].variant === 'danger' ? <AlertCircleIcon /> : <WarningTriangleIcon />}
            title={tAbsolute(REFUSALS[state.reason].key)}
          />
        </div>
      )}
      {state.kind === 'failed' && (
        <div className="mt-4">
          <Banner
            variant="danger"
            icon={<AlertCircleIcon />}
            title={tErrors(LANDIQ_PAGE_ERROR_CODES.find((code) => code === state.code) ?? 'generic.unexpected')}
          />
        </div>
      )}
    </Modal>
  );
}
